import { joinPaths } from '@standardnotes/utils';
import { AbstractService, StorageKey, ApiServiceEvent, API_MESSAGE_GENERIC_SYNC_FAIL, API_MESSAGE_GENERIC_TOKEN_REFRESH_FAIL, API_MESSAGE_CHANGE_CREDENTIALS_IN_PROGRESS, API_MESSAGE_FAILED_ACCESS_PURCHASE, API_MESSAGE_FAILED_CREATE_FILE_TOKEN, API_MESSAGE_FAILED_GET_SETTINGS, API_MESSAGE_FAILED_LISTED_REGISTRATION, API_MESSAGE_FAILED_OFFLINE_ACTIVATION, API_MESSAGE_FAILED_OFFLINE_FEATURES, API_MESSAGE_FAILED_UPDATE_SETTINGS, API_MESSAGE_GENERIC_CHANGE_CREDENTIALS_FAIL, API_MESSAGE_GENERIC_INTEGRITY_CHECK_FAIL, API_MESSAGE_GENERIC_INVALID_LOGIN, API_MESSAGE_GENERIC_SINGLE_ITEM_SYNC_FAIL, API_MESSAGE_INVALID_SESSION, API_MESSAGE_LOGIN_IN_PROGRESS, API_MESSAGE_TOKEN_REFRESH_IN_PROGRESS, } from '@standardnotes/services';
import { HttpStatusCode, ErrorTag, HttpVerb, ApiEndpointParam, ClientDisplayableError, isErrorResponse, } from '@standardnotes/responses';
import { Session, SessionToken } from '@standardnotes/domain-core';
import { Paths } from './Paths';
import { Strings } from '@Lib/Strings';
/** Legacy api version field to be specified in params when calling v0 APIs. */
const V0_API_VERSION = '20240226';
export class LegacyApiService extends AbstractService {
    constructor(httpService, storageService, host, inMemoryStore, crypto, sessionStorageMapper, legacySessionStorageMapper, internalEventBus) {
        super(internalEventBus);
        this.httpService = httpService;
        this.storageService = storageService;
        this.host = host;
        this.inMemoryStore = inMemoryStore;
        this.crypto = crypto;
        this.sessionStorageMapper = sessionStorageMapper;
        this.legacySessionStorageMapper = legacySessionStorageMapper;
        this.internalEventBus = internalEventBus;
        this.authenticating = false;
        this.changing = false;
        this.refreshingSession = false;
        this.session = null;
    }
    deinit() {
        ;
        this.httpService = undefined;
        this.storageService = undefined;
        this.invalidSessionObserver = undefined;
        this.session = null;
        super.deinit();
    }
    setUser(user) {
        this.user = user;
    }
    /**
     * When a we receive a 401 error from the server, we'll notify the observer.
     * Note that this applies only to sessions that are totally invalid. Sessions that
     * are expired but can be renewed are still considered to be valid. In those cases,
     * the server response is 498.
     * If the session has been revoked, then the observer will have its first
     * argument set to true.
     */
    setInvalidSessionObserver(observer) {
        this.invalidSessionObserver = observer;
    }
    loadHost() {
        const storedValue = this.storageService.getValue(StorageKey.ServerHost);
        this.host = storedValue || this.host;
        return this.host;
    }
    async setHost(host) {
        this.host = host;
        this.storageService.setValue(StorageKey.ServerHost, host);
    }
    getHost() {
        return this.host;
    }
    getFilesHost() {
        if (!this.filesHost) {
            throw Error('Attempting to access undefined filesHost');
        }
        return this.filesHost;
    }
    setSession(session, persist = true) {
        this.session = session;
        if (persist) {
            let sessionProjection;
            if (session instanceof Session) {
                sessionProjection = this.sessionStorageMapper.toProjection(session);
            }
            else {
                sessionProjection = this.legacySessionStorageMapper.toProjection(session);
            }
            this.storageService.setValue(StorageKey.Session, sessionProjection);
        }
    }
    getSession() {
        return this.session;
    }
    get apiVersion() {
        return V0_API_VERSION;
    }
    params(inParams) {
        const params = {
            ...inParams,
            ...{
                [ApiEndpointParam.ApiVersion]: this.apiVersion,
            },
        };
        return params;
    }
    createErrorResponse(message, status, tag) {
        return { data: { error: { message, tag } }, status: status !== null && status !== void 0 ? status : HttpStatusCode.BadRequest };
    }
    errorResponseWithFallbackMessage(response, message) {
        if (response.data.error && !response.data.error.message) {
            response.data.error.message = message;
        }
        return response;
    }
    processMetaObject(meta) {
        var _a, _b;
        if (meta.auth && meta.auth.userUuid && meta.auth.roles) {
            void this.notifyEvent(ApiServiceEvent.MetaReceived, {
                userUuid: meta.auth.userUuid,
                userRoles: meta.auth.roles,
            });
        }
        if ((_a = meta.server) === null || _a === void 0 ? void 0 : _a.filesServerUrl) {
            this.filesHost = (_b = meta.server) === null || _b === void 0 ? void 0 : _b.filesServerUrl;
        }
    }
    processSuccessResponseForMetaBody(response) {
        if (response.meta) {
            this.processMetaObject(response.meta);
        }
    }
    async request(params) {
        try {
            const response = await this.httpService.runHttp(params);
            if (isErrorResponse(response)) {
                return this.errorResponseWithFallbackMessage(response, params.fallbackErrorMessage);
            }
            else {
                this.processSuccessResponseForMetaBody(response);
                return response;
            }
        }
        catch (errorResponse) {
            return this.errorResponseWithFallbackMessage(errorResponse, params.fallbackErrorMessage);
        }
    }
    /**
     * @param mfaCode     The mfa challenge response value.
     */
    async getAccountKeyParams(dto) {
        const codeVerifier = this.crypto.generateRandomKey(256);
        this.inMemoryStore.setValue(StorageKey.CodeVerifier, codeVerifier);
        const codeChallenge = this.crypto.base64URLEncode(await this.crypto.sha256(codeVerifier));
        const params = this.params({
            email: dto.email,
            code_challenge: codeChallenge,
        });
        if (dto.mfaCode !== undefined) {
            params['mfa_code'] = dto.mfaCode;
        }
        if (dto.authenticatorResponse) {
            params.authenticator_response = dto.authenticatorResponse;
        }
        return this.request({
            verb: HttpVerb.Post,
            url: joinPaths(this.host, Paths.v2.keyParams),
            fallbackErrorMessage: API_MESSAGE_GENERIC_INVALID_LOGIN,
            params,
            /** A session is optional here, if valid, endpoint bypasses 2FA and returns additional params */
            authentication: this.getSessionAccessToken(),
        });
    }
    async signIn(dto) {
        if (this.authenticating) {
            return this.createErrorResponse(API_MESSAGE_LOGIN_IN_PROGRESS, HttpStatusCode.BadRequest);
        }
        this.authenticating = true;
        const url = joinPaths(this.host, Paths.v2.signIn);
        const params = this.params({
            email: dto.email,
            password: dto.serverPassword,
            ephemeral: dto.ephemeral,
            code_verifier: this.inMemoryStore.getValue(StorageKey.CodeVerifier),
            hvm_token: dto.hvmToken,
        });
        const response = await this.request({
            verb: HttpVerb.Post,
            url,
            params,
            fallbackErrorMessage: API_MESSAGE_GENERIC_INVALID_LOGIN,
        });
        this.authenticating = false;
        this.inMemoryStore.removeValue(StorageKey.CodeVerifier);
        return response;
    }
    signOut() {
        return this.httpService.post(Paths.v1.signOut, undefined, {
            authentication: this.getSessionAccessToken(),
        });
    }
    async changeCredentials(parameters) {
        if (this.changing) {
            return this.createErrorResponse(API_MESSAGE_CHANGE_CREDENTIALS_IN_PROGRESS, HttpStatusCode.BadRequest);
        }
        const preprocessingError = this.preprocessingError();
        if (preprocessingError) {
            return preprocessingError;
        }
        this.changing = true;
        const path = Paths.v1.changeCredentials(parameters.userUuid);
        const params = this.params({
            current_password: parameters.currentServerPassword,
            new_password: parameters.newServerPassword,
            new_email: parameters.newEmail,
            ...parameters.newKeyParams.getPortableValue(),
        });
        const response = await this.httpService.put(path, params, {
            authentication: this.getSessionAccessToken(),
        });
        this.changing = false;
        if (isErrorResponse(response)) {
            return this.errorResponseWithFallbackMessage(response, API_MESSAGE_GENERIC_CHANGE_CREDENTIALS_FAIL);
        }
        this.processSuccessResponseForMetaBody(response);
        return response;
    }
    async sync(payloads, lastSyncToken, paginationToken, limit, sharedVaultUuids) {
        const preprocessingError = this.preprocessingError();
        if (preprocessingError) {
            return preprocessingError;
        }
        const request = this.getSyncHttpRequest(payloads, lastSyncToken, paginationToken, limit, sharedVaultUuids);
        const response = await this.httpService.runHttp(request);
        if (isErrorResponse(response)) {
            this.preprocessAuthenticatedErrorResponse(response);
            return this.errorResponseWithFallbackMessage(response, API_MESSAGE_GENERIC_SYNC_FAIL());
        }
        this.processSuccessResponseForMetaBody(response);
        return response;
    }
    getSyncHttpRequest(payloads, lastSyncToken, paginationToken, limit, sharedVaultUuids) {
        const path = Paths.v1.sync;
        const params = this.params({
            [ApiEndpointParam.SyncPayloads]: payloads,
            [ApiEndpointParam.LastSyncToken]: lastSyncToken,
            [ApiEndpointParam.PaginationToken]: paginationToken,
            [ApiEndpointParam.SyncDlLimit]: limit,
            [ApiEndpointParam.SharedVaultUuids]: sharedVaultUuids,
        });
        return {
            url: joinPaths(this.host, path),
            params,
            verb: HttpVerb.Post,
            authentication: this.getSessionAccessToken(),
        };
    }
    /**
     * @deprecated
     *
     * This function should be replaced with @standardnotes/api's `HttpService::refreshSession` function.
     */
    async deprecatedRefreshSessionOnlyUsedInE2eTests() {
        const preprocessingError = this.preprocessingError();
        if (preprocessingError) {
            return preprocessingError;
        }
        this.refreshingSession = true;
        const session = this.session;
        const params = this.params({
            access_token: session.accessToken.value,
            refresh_token: session.refreshToken.value,
        });
        const response = await this.httpService
            .post(Paths.v1.refreshSession, params)
            .then(async (response) => {
            if (isErrorResponse(response) || !response.data.session) {
                return response;
            }
            const accessTokenOrError = SessionToken.create(response.data.session.access_token, response.data.session.access_expiration);
            if (accessTokenOrError.isFailed()) {
                return null;
            }
            const accessToken = accessTokenOrError.getValue();
            const refreshTokenOrError = SessionToken.create(response.data.session.refresh_token, response.data.session.refresh_expiration);
            if (refreshTokenOrError.isFailed()) {
                return null;
            }
            const refreshToken = refreshTokenOrError.getValue();
            const sessionOrError = Session.create(accessToken, refreshToken, response.data.session.readonly_access);
            if (sessionOrError.isFailed()) {
                return null;
            }
            const session = sessionOrError.getValue();
            this.session = session;
            this.setSession(session);
            this.processSuccessResponseForMetaBody(response);
            await this.notifyEventSync(ApiServiceEvent.SessionRefreshed, {
                session,
            });
            return response;
        });
        this.refreshingSession = false;
        if (response === null) {
            return this.createErrorResponse(API_MESSAGE_INVALID_SESSION, HttpStatusCode.BadRequest);
        }
        if (isErrorResponse(response)) {
            this.preprocessAuthenticatedErrorResponse(response);
            return this.errorResponseWithFallbackMessage(response, API_MESSAGE_GENERIC_TOKEN_REFRESH_FAIL);
        }
        return response;
    }
    async getSessionsList() {
        const preprocessingError = this.preprocessingError();
        if (preprocessingError) {
            return preprocessingError;
        }
        const path = Paths.v1.sessions;
        const response = await this.httpService.get(path, {}, { authentication: this.getSessionAccessToken() });
        if (isErrorResponse(response)) {
            this.preprocessAuthenticatedErrorResponse(response);
            return this.errorResponseWithFallbackMessage(response, API_MESSAGE_GENERIC_SYNC_FAIL());
        }
        this.processSuccessResponseForMetaBody(response);
        return response;
    }
    async deleteSession(sessionId) {
        const preprocessingError = this.preprocessingError();
        if (preprocessingError) {
            return preprocessingError;
        }
        const path = Paths.v1.session(sessionId);
        const response = await this.httpService.delete(path, { uuid: sessionId }, { authentication: this.getSessionAccessToken() });
        if (isErrorResponse(response)) {
            this.preprocessAuthenticatedErrorResponse(response);
            return this.errorResponseWithFallbackMessage(response, API_MESSAGE_GENERIC_SYNC_FAIL());
        }
        this.processSuccessResponseForMetaBody(response);
        return response;
    }
    async tokenRefreshableRequest(params) {
        const preprocessingError = this.preprocessingError();
        if (preprocessingError) {
            return preprocessingError;
        }
        const response = await this.httpService.runHttp(params);
        if (isErrorResponse(response)) {
            this.preprocessAuthenticatedErrorResponse(response);
            return this.errorResponseWithFallbackMessage(response, params.fallbackErrorMessage);
        }
        this.processSuccessResponseForMetaBody(response);
        return response;
    }
    async listSettings(userUuid) {
        return await this.tokenRefreshableRequest({
            verb: HttpVerb.Get,
            url: joinPaths(this.host, Paths.v1.settings(userUuid)),
            fallbackErrorMessage: API_MESSAGE_FAILED_GET_SETTINGS,
            authentication: this.getSessionAccessToken(),
        });
    }
    async updateSetting(userUuid, settingName, settingValue, sensitive, totpToken) {
        const params = {
            name: settingName,
            value: settingValue,
            sensitive: sensitive,
            ...(totpToken && { totpToken }),
        };
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Put,
            url: joinPaths(this.host, Paths.v1.settings(userUuid)),
            authentication: this.getSessionAccessToken(),
            fallbackErrorMessage: API_MESSAGE_FAILED_UPDATE_SETTINGS,
            params,
        });
    }
    async getSetting(userUuid, settingName, serverPassword) {
        const customHeaders = serverPassword ? [{ key: 'x-server-password', value: serverPassword }] : undefined;
        return await this.tokenRefreshableRequest({
            verb: HttpVerb.Get,
            url: joinPaths(this.host, Paths.v1.setting(userUuid, settingName.toLowerCase())),
            authentication: this.getSessionAccessToken(),
            fallbackErrorMessage: API_MESSAGE_FAILED_GET_SETTINGS,
            customHeaders,
        });
    }
    async getSubscriptionSetting(userUuid, settingName) {
        return await this.tokenRefreshableRequest({
            verb: HttpVerb.Get,
            url: joinPaths(this.host, Paths.v1.subscriptionSetting(userUuid, settingName.toLowerCase())),
            authentication: this.getSessionAccessToken(),
            fallbackErrorMessage: API_MESSAGE_FAILED_GET_SETTINGS,
        });
    }
    async updateSubscriptionSetting(userUuid, settingName, settingValue, sensitive) {
        const params = {
            name: settingName,
            value: settingValue,
            sensitive: sensitive,
        };
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Put,
            url: joinPaths(this.host, Paths.v1.subscriptionSettings(userUuid)),
            authentication: this.getSessionAccessToken(),
            fallbackErrorMessage: API_MESSAGE_FAILED_UPDATE_SETTINGS,
            params,
        });
    }
    async deleteSetting(userUuid, settingName, serverPassword) {
        const customHeaders = serverPassword ? [{ key: 'x-server-password', value: serverPassword }] : undefined;
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Delete,
            url: joinPaths(this.host, Paths.v1.setting(userUuid, settingName)),
            authentication: this.getSessionAccessToken(),
            fallbackErrorMessage: API_MESSAGE_FAILED_UPDATE_SETTINGS,
            customHeaders,
        });
    }
    async getMfaSecret(userUuid) {
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Get,
            url: joinPaths(this.host, Paths.v1.mfaSecret(userUuid)),
            authentication: this.getSessionAccessToken(),
            fallbackErrorMessage: 'Failed to get MFA secret.',
        });
    }
    downloadFeatureUrl(url) {
        return this.request({
            verb: HttpVerb.Get,
            url,
            external: true,
            fallbackErrorMessage: API_MESSAGE_GENERIC_INVALID_LOGIN,
        });
    }
    async getNewSubscriptionToken() {
        const url = joinPaths(this.host, Paths.v1.subscriptionTokens);
        const response = await this.request({
            verb: HttpVerb.Post,
            url,
            authentication: this.getSessionAccessToken(),
            fallbackErrorMessage: API_MESSAGE_FAILED_ACCESS_PURCHASE,
        });
        if (isErrorResponse(response)) {
            return undefined;
        }
        return response.data.token;
    }
    async downloadOfflineFeaturesFromRepo(dto) {
        try {
            const featuresUrl = dto.repo.offlineFeaturesUrl;
            const extensionKey = dto.repo.offlineKey;
            if (!featuresUrl || !extensionKey) {
                throw Error('Cannot download offline repo without url and offlineKEy');
            }
            const TRUSTED_FEATURE_HOSTS = ['api.notes.noimfamily.com', 'localhost'];
            const { hostname } = new URL(featuresUrl);
            if (!TRUSTED_FEATURE_HOSTS.includes(hostname)) {
                return new ClientDisplayableError(`The offline features host ${hostname} is not in the trusted allowlist.`);
            }
            const response = await this.request({
                verb: HttpVerb.Get,
                url: featuresUrl,
                fallbackErrorMessage: API_MESSAGE_FAILED_OFFLINE_FEATURES,
                customHeaders: [{ key: 'x-offline-token', value: extensionKey }],
            });
            if (isErrorResponse(response)) {
                return ClientDisplayableError.FromNetworkError(response);
            }
            const data = response.data;
            return {
                features: (data === null || data === void 0 ? void 0 : data.features) || [],
                roles: (data === null || data === void 0 ? void 0 : data.roles) || [],
            };
        }
        catch {
            return new ClientDisplayableError(API_MESSAGE_FAILED_OFFLINE_ACTIVATION);
        }
    }
    async registerForListedAccount() {
        if (!this.user) {
            throw Error('Cannot register for Listed without user account.');
        }
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Post,
            url: joinPaths(this.host, Paths.v1.listedRegistration(this.user.uuid)),
            fallbackErrorMessage: API_MESSAGE_FAILED_LISTED_REGISTRATION,
            authentication: this.getSessionAccessToken(),
        });
    }
    async createUserFileValetToken(remoteIdentifier, operation, unencryptedFileSize) {
        var _a, _b, _c, _d, _e, _f;
        const url = joinPaths(this.host, Paths.v1.createUserFileValetToken);
        const params = {
            operation,
            resources: [{ remoteIdentifier, unencryptedFileSize: unencryptedFileSize || 0 }],
        };
        const response = await this.tokenRefreshableRequest({
            verb: HttpVerb.Post,
            url: url,
            authentication: this.getSessionAccessToken(),
            fallbackErrorMessage: API_MESSAGE_FAILED_CREATE_FILE_TOKEN,
            params,
        });
        if (isErrorResponse(response)) {
            return new ClientDisplayableError((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.message);
        }
        if (!((_c = response.data) === null || _c === void 0 ? void 0 : _c.success)) {
            return new ClientDisplayableError((_d = response.data) === null || _d === void 0 ? void 0 : _d.reason, undefined, (_e = response.data) === null || _e === void 0 ? void 0 : _e.reason);
        }
        return (_f = response.data) === null || _f === void 0 ? void 0 : _f.valetToken;
    }
    async startUploadSession(valetToken, ownershipType) {
        const url = joinPaths(this.getFilesHost(), ownershipType === 'user' ? Paths.v1.startUploadSession : Paths.v1.startSharedVaultUploadSession);
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Post,
            url,
            customHeaders: [{ key: 'x-valet-token', value: valetToken }],
            fallbackErrorMessage: Strings.Network.Files.FailedStartUploadSession,
        });
    }
    async deleteFile(valetToken, ownershipType) {
        const url = joinPaths(this.getFilesHost(), ownershipType === 'user' ? Paths.v1.deleteFile : Paths.v1.deleteSharedVaultFile);
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Delete,
            url,
            customHeaders: [{ key: 'x-valet-token', value: valetToken }],
            fallbackErrorMessage: Strings.Network.Files.FailedDeleteFile,
        });
    }
    async uploadFileBytes(valetToken, ownershipType, chunkId, encryptedBytes) {
        if (chunkId === 0) {
            throw Error('chunkId must start with 1');
        }
        const url = joinPaths(this.getFilesHost(), ownershipType === 'user' ? Paths.v1.uploadFileChunk : Paths.v1.uploadSharedVaultFileChunk);
        const response = await this.tokenRefreshableRequest({
            verb: HttpVerb.Post,
            url,
            rawBytes: encryptedBytes,
            customHeaders: [
                { key: 'x-valet-token', value: valetToken },
                { key: 'x-chunk-id', value: chunkId.toString() },
                { key: 'Content-Type', value: 'application/octet-stream' },
            ],
            fallbackErrorMessage: Strings.Network.Files.FailedUploadFileChunk,
        });
        if (isErrorResponse(response)) {
            return false;
        }
        return response.data.success;
    }
    async closeUploadSession(valetToken, ownershipType) {
        const url = joinPaths(this.getFilesHost(), ownershipType === 'user' ? Paths.v1.closeUploadSession : Paths.v1.closeSharedVaultUploadSession);
        const response = await this.tokenRefreshableRequest({
            verb: HttpVerb.Post,
            url,
            customHeaders: [{ key: 'x-valet-token', value: valetToken }],
            fallbackErrorMessage: Strings.Network.Files.FailedCloseUploadSession,
        });
        if (isErrorResponse(response)) {
            return ClientDisplayableError.FromNetworkError(response);
        }
        return response.data.success;
    }
    async moveFile(valetToken) {
        const url = joinPaths(this.getFilesHost(), Paths.v1.moveFile);
        const response = await this.tokenRefreshableRequest({
            verb: HttpVerb.Post,
            url,
            customHeaders: [{ key: 'x-valet-token', value: valetToken }],
            fallbackErrorMessage: Strings.Network.Files.FailedCloseUploadSession,
        });
        if (isErrorResponse(response)) {
            return false;
        }
        return response.data.success;
    }
    getFilesDownloadUrl(ownershipType) {
        if (ownershipType === 'user') {
            return joinPaths(this.getFilesHost(), Paths.v1.downloadFileChunk);
        }
        else if (ownershipType === 'shared-vault') {
            return joinPaths(this.getFilesHost(), Paths.v1.downloadSharedVaultFileChunk);
        }
        else {
            throw Error('Invalid download type');
        }
    }
    async downloadFile({ file, chunkIndex, valetToken, ownershipType, contentRangeStart, onBytesReceived, }) {
        var _a, _b;
        const url = this.getFilesDownloadUrl(ownershipType);
        const pullChunkSize = file.encryptedChunkSizes[chunkIndex];
        const request = {
            verb: HttpVerb.Get,
            url,
            customHeaders: [
                { key: 'x-valet-token', value: valetToken },
                {
                    key: 'x-chunk-size',
                    value: pullChunkSize.toString(),
                },
                { key: 'range', value: `bytes=${contentRangeStart}-` },
            ],
            responseType: 'arraybuffer',
        };
        const response = await this.tokenRefreshableRequest({
            ...request,
            fallbackErrorMessage: Strings.Network.Files.FailedDownloadFileChunk,
        });
        if (isErrorResponse(response)) {
            return new ClientDisplayableError((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.message);
        }
        const contentRangeHeader = response.headers.get('content-range');
        if (!contentRangeHeader) {
            return new ClientDisplayableError('Could not obtain content-range header while downloading file chunk');
        }
        const matches = contentRangeHeader.match(/(^[a-zA-Z][\w]*)\s+(\d+)\s?-\s?(\d+)?\s?\/?\s?(\d+|\*)?/);
        if (!matches || matches.length !== 5) {
            return new ClientDisplayableError('Malformed content-range header in response when downloading file chunk');
        }
        const rangeStart = +matches[2];
        const rangeEnd = +matches[3];
        const totalSize = +matches[4];
        const bytesReceived = new Uint8Array(response.data);
        await onBytesReceived(bytesReceived);
        if (rangeEnd < totalSize - 1) {
            return this.downloadFile({
                file,
                chunkIndex: ++chunkIndex,
                valetToken,
                ownershipType,
                contentRangeStart: rangeStart + pullChunkSize,
                onBytesReceived,
            });
        }
        return undefined;
    }
    async checkIntegrity(integrityPayloads) {
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Post,
            url: joinPaths(this.host, Paths.v1.checkIntegrity),
            params: {
                integrityPayloads,
            },
            fallbackErrorMessage: API_MESSAGE_GENERIC_INTEGRITY_CHECK_FAIL,
            authentication: this.getSessionAccessToken(),
        });
    }
    async getSingleItem(itemUuid) {
        return this.tokenRefreshableRequest({
            verb: HttpVerb.Get,
            url: joinPaths(this.host, Paths.v1.getSingleItem(itemUuid)),
            fallbackErrorMessage: API_MESSAGE_GENERIC_SINGLE_ITEM_SYNC_FAIL(),
            authentication: this.getSessionAccessToken(),
        });
    }
    preprocessingError() {
        if (this.refreshingSession) {
            return this.createErrorResponse(API_MESSAGE_TOKEN_REFRESH_IN_PROGRESS, HttpStatusCode.BadRequest);
        }
        if (!this.session) {
            return this.createErrorResponse(API_MESSAGE_INVALID_SESSION, HttpStatusCode.BadRequest);
        }
        return undefined;
    }
    preprocessAuthenticatedErrorResponse(response) {
        var _a, _b;
        if (!this.session) {
            return;
        }
        /**
         * In most cases the ExpiredAccessToken erorr shouldn't reach this function, since if a 498 is caught, a refresh
         * will automatically take place. However there does appear to be rare cases where for some reason the 498 falls through,
         * perhaps because for example the server responds to a refresh request with a 498. In those cases, we'll just
         * fallback here to the invalid session observer so that the user can be reprompted for auth.
         */
        if (response.status === HttpStatusCode.Unauthorized || response.status === HttpStatusCode.ExpiredAccessToken) {
            (_a = this.invalidSessionObserver) === null || _a === void 0 ? void 0 : _a.call(this, ((_b = response.data.error) === null || _b === void 0 ? void 0 : _b.tag) === ErrorTag.RevokedSession);
        }
    }
    getSessionAccessToken() {
        if (!this.session) {
            return undefined;
        }
        if (this.session instanceof Session) {
            return this.session.accessToken.value;
        }
        return this.session.accessToken;
    }
    getCaptchaUrl() {
        const response = this.httpService.get(Paths.v1.meta);
        return response;
    }
}
